import type {
  ThreadResponse,
  EvaluationResponse,
  ThreadScorecardResponse,
  BatchResponse,
} from "../../gen/models";
import { ActiveBatchSelectModal } from "./ActiveBatchSelectModal";
import { BatchAssignModal } from "./BatchAssignModal";
import { ConfirmModal } from "../ConfirmModal";
import { ThreadDetailOverlays } from "./ThreadDetailOverlays";
import {
  GovernanceInspectionModal,
  type InspectionTarget,
} from "./GovernanceInspectionModal";

interface ThreadDetailModalsProps {
  // Thread & Governance Data
  thread: ThreadResponse | null;
  scorecard: ThreadScorecardResponse | null;
  evaluations: EvaluationResponse[];
  activeBatches: BatchResponse[];
  allBatches?: BatchResponse[];
  activeBatchId: string;
  activeBatch: BatchResponse | null;

  // Active Batch Select Modal
  isActiveBatchModalOpen: boolean;
  onCloseActiveBatchModal: () => void;
  onSelectActiveBatch: (batchId: string) => void;
  onCreateActiveBatch: (
    title: string,
    description?: string,
    assignAll?: boolean
  ) => Promise<string | null>;

  // Batch Assign Modal
  isBatchAssignModalOpen: boolean;
  targetBatchId: string;
  newBatchTitle: string;
  creatingBatchInline: boolean;
  assigningEvaluations: boolean;
  assignAllEvaluations: boolean;
  onSetAssignAllEvaluations: (val: boolean) => void;
  onCloseBatchAssignModal: () => void;
  onSetTargetBatchId: (id: string) => void;
  onSetNewBatchTitle: (val: string) => void;
  onSetCreatingBatchInline: (val: boolean) => void;
  onAssignEvaluations: () => Promise<void>;
  onOpenBatchAssignModal?: (evals?: EvaluationResponse[]) => void;
  onAssignDirectToActiveBatch?: (evalIds: string[]) => Promise<void>;

  // Delete Comment Modal
  deleteModalCommentId: string | null;
  deletingComment: boolean;
  onCancelDeleteComment: () => void;
  onConfirmDeleteComment: () => Promise<void>;

  // Drawers (Evaluation + Simulator)
  isEvalDrawerOpen: boolean;
  selectedEvaluation: EvaluationResponse | null;
  evalDrawerMessageId: string | null;
  evalDrawerRunId: string | null;
  evalDrawerIsHumanMode: boolean;
  isSimulatorOpen: boolean;
  simulatorTurnId?: string;
  simulatorInquiry?: string;
  simulatorBaseline?: string;
  onCloseEvalDrawer: () => void;
  onCloseSimulator: () => void;
  onReviewSubmitted: () => void;
  onRequireActiveBatch: (callback?: (batchId: string) => void) => void;

  // In-situ Item Inspection Modal
  inspectionTarget: InspectionTarget | null;
  onCloseInspection: () => void;
  onInspectTarget?: (target: InspectionTarget) => void;
}

export function ThreadDetailModals({
  thread,
  scorecard,
  evaluations,
  activeBatches,
  allBatches,
  activeBatchId,
  activeBatch,
  isActiveBatchModalOpen,
  onCloseActiveBatchModal,
  onSelectActiveBatch,
  onCreateActiveBatch,
  isBatchAssignModalOpen,
  targetBatchId,
  newBatchTitle,
  creatingBatchInline,
  assigningEvaluations,
  assignAllEvaluations,
  onSetAssignAllEvaluations,
  onCloseBatchAssignModal,
  onSetTargetBatchId,
  onSetNewBatchTitle,
  onSetCreatingBatchInline,
  onAssignEvaluations,
  onOpenBatchAssignModal,
  onAssignDirectToActiveBatch,
  deleteModalCommentId,
  deletingComment,
  onCancelDeleteComment,
  onConfirmDeleteComment,
  isEvalDrawerOpen,
  selectedEvaluation,
  evalDrawerMessageId,
  evalDrawerRunId,
  evalDrawerIsHumanMode,
  isSimulatorOpen,
  simulatorTurnId,
  simulatorInquiry,
  simulatorBaseline,
  onCloseEvalDrawer,
  onCloseSimulator,
  onReviewSubmitted,
  onRequireActiveBatch,
  inspectionTarget,
  onCloseInspection,
  onInspectTarget,
}: ThreadDetailModalsProps) {
  return (
    <>
      {/* 1. Active Batch Selection / Prompt Modal */}
      <ActiveBatchSelectModal
        isOpen={isActiveBatchModalOpen}
        activeBatches={activeBatches}
        activeBatchId={activeBatchId}
        onClose={onCloseActiveBatchModal}
        onSelectBatch={onSelectActiveBatch}
        onCreateBatch={onCreateActiveBatch}
      />

      {/* 2. Batch Assignment Modal */}
      <BatchAssignModal
        isOpen={isBatchAssignModalOpen}
        evaluations={evaluations}
        activeBatches={activeBatches}
        targetBatchId={targetBatchId}
        newBatchTitle={newBatchTitle}
        creatingBatchInline={creatingBatchInline}
        assigningEvaluations={assigningEvaluations}
        assignAllEvaluations={assignAllEvaluations}
        onSetAssignAllEvaluations={onSetAssignAllEvaluations}
        onClose={onCloseBatchAssignModal}
        onSetTargetBatchId={onSetTargetBatchId}
        onSetNewBatchTitle={onSetNewBatchTitle}
        onSetCreatingBatchInline={onSetCreatingBatchInline}
        onAssignEvaluations={onAssignEvaluations}
      />

      {/* 3. Delete Comment Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(deleteModalCommentId)}
        onClose={onCancelDeleteComment}
        onConfirm={onConfirmDeleteComment}
        title="Delete Comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deletingComment}
      />

      {/* 4. Evaluation and Simulator Drawers */}
      <ThreadDetailOverlays
        thread={thread}
        scorecard={scorecard}
        evaluations={evaluations}
        isEvalDrawerOpen={isEvalDrawerOpen}
        selectedEvaluation={selectedEvaluation}
        evalDrawerMessageId={evalDrawerMessageId}
        evalDrawerRunId={evalDrawerRunId}
        evalDrawerIsHumanMode={evalDrawerIsHumanMode}
        isSimulatorOpen={isSimulatorOpen}
        simulatorTurnId={simulatorTurnId}
        simulatorInquiry={simulatorInquiry}
        simulatorBaseline={simulatorBaseline}
        onCloseEvalDrawer={onCloseEvalDrawer}
        onCloseSimulator={onCloseSimulator}
        onReviewSubmitted={onReviewSubmitted}
        activeBatch={activeBatch}
        activeBatches={activeBatches}
        allBatches={allBatches}
        onRequireActiveBatch={onRequireActiveBatch}
        onOpenBatchAssignModal={onOpenBatchAssignModal}
        onAssignDirectToActiveBatch={onAssignDirectToActiveBatch}
      />

      {/* 5. In-situ Rule / Knowledge / Chunk Inspection Modal */}
      <GovernanceInspectionModal
        target={inspectionTarget}
        onClose={onCloseInspection}
        onInspect={onInspectTarget}
      />
    </>
  );
}
